import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Col, Form} from 'react-bootstrap';
import {
    DrawerRunnerRequest,
    useLazyGetRunnerQuery,
} from 'components/customer/Product/store/drawerRunnerApi';
import {
    DrawerKey,
    DrawerFields,
    HardwareType,
} from 'components/customer/Product/entity/Drawer';
import {useFormikContext} from 'formik';
import {useRunnerRequestParams} from 'components/customer/Product/Drawer/helpers/useRunnerRequestParams';
import {runnerIdsMatch} from 'components/customer/Product/Drawer/helpers';
import {runnerWrites} from 'components/customer/Product/Drawer/helpers/hardwareWrites';
import {useDrawerDefaults} from 'components/customer/Product/Drawer/DrawerDefaultsContext';
import {useNotificationContext, useProductContext} from 'contexts';
import {validationErrorAppliesToField} from 'shared/validator';
import {isEmpty} from 'lodash';
import SelectField from 'shared/components/SelectField';
import {Tooltip} from 'shared/components/Tooltip';
import {FormGroup} from 'shared/components/Forms/FormGroup';
import {DrawerRunner as DrawerRunnerInterface} from 'components/customer/Product/entity/DrawerRunner';
import {useDebouncedCallback} from 'use-debounce';
import {useSearchParams} from 'react-router-dom';
import {useQueryParams} from 'shared/helpers';

interface DrawerRunnerProps {
    isInnerDrawer?: boolean;
    isDrawerComponent?: boolean;
    runnerIndex?: number;
    fieldName: string;
    drawerTypeFieldName: string;
    drawerFaceHeightFieldName: string;
    fingerPullFieldName: string;
    drawerDepth: string;
    labelColSpan?: number;
}

export const DrawerRunner = ({
    isInnerDrawer = false,
    isDrawerComponent = false,
    runnerIndex = 0,
    fieldName,
    drawerTypeFieldName,
    drawerFaceHeightFieldName,
    fingerPullFieldName,
    drawerDepth,
    labelColSpan = 5,
}: DrawerRunnerProps) => {
    const {values, setFieldValue} = useFormikContext<DrawerFields>();
    const {runnerReset} = useDrawerDefaults();
    const {params, latest} = useRunnerRequestParams(
        runnerIndex,
        isInnerDrawer,
        isDrawerComponent,
        drawerTypeFieldName,
        drawerDepth,
        drawerFaceHeightFieldName,
        fingerPullFieldName
    );

    const [searchParams] = useSearchParams();
    const isEditingProduct = useMemo(() => {
        return searchParams.has('cabinetId');
    }, [searchParams]);

    const {messages} = useNotificationContext();
    const {addValidationData, getValidationData} = useProductContext();

    const [runners, setRunners] = useState<DrawerRunnerInterface[]>([]);
    const [getRunners, {isFetching, isLoading, isUninitialized}] =
        useLazyGetRunnerQuery();
    const {cabinetId} = useQueryParams() as {cabinetId: number};
    const prevDepthRef = useRef<number | undefined>(undefined);

    const value = useMemo(() => {
        if (values?.drawers?.length) {
            const drawer = values.drawers[Number(runnerIndex)];
            if (drawer) {
                return drawer[fieldName as DrawerKey];
            }
        }

        return null;
    }, [values, isDrawerComponent]);

    const errorRef = useRef(false);
    const resetSeen = useRef(runnerReset);
    const resetPending = useRef(false);
    const triggerGetRunners = useDebouncedCallback(
        async (runnerProps: DrawerRunnerRequest) => {
            if (runnerProps.insertType == null || runnerProps.faceHeight <= 0) {
                if (!isUninitialized) {
                    void setFieldValue(
                        `drawers[${runnerIndex}.${fieldName}]`,
                        -1
                    );
                    void setFieldValue(`drawers[${runnerIndex}].hardware`, [
                        [],
                    ]);
                    setRunners([]);
                }
                return;
            }

            const cabinetDrawerBottom = Number(values.cabinet_drawer_bottom);
            const cabinetDrawerTop = Number(values.cabinet_drawer_top);
            const drawerCount = values?.drawers?.length ?? 0;

            /**
             * Clearance rules:
             * - Single drawer → top & bottom apply
             * - Multiple drawers:
             *   - top clearance → top drawer only
             *   - bottom clearance → bottom drawer only
             */
            if (cabinetDrawerTop < 0 && runnerIndex == 0) {
                runnerProps.faceHeight += cabinetDrawerTop;
            }

            if (cabinetDrawerBottom < 0 && runnerIndex == drawerCount - 1) {
                runnerProps.faceHeight += cabinetDrawerBottom;
            }

            const {data} = await getRunners(runnerProps, true);
            const resetting = resetPending.current;

            // a list for a face the preset has since changed does not settle it
            if (runnerProps === latest.current) resetPending.current = false;

            // a failed request is undefined, not empty; either way there is no
            // runner behind the old selection, so it cannot be left showing
            if (!data?.length && runnerIndex !== undefined) {
                errorRef.current = true;
                void setFieldValue(`drawers[${runnerIndex}.${fieldName}]`, -1);
                void setFieldValue(`drawers[${runnerIndex}].hardware`, [[]]);
                setRunners([]);
            } else {
                setRunners(data);
                if (errorRef.current) {
                    errorRef.current = false;

                    const firstRunner = data[0];

                    if (firstRunner) {
                        selectRunner('', firstRunner.id, data);
                    }
                }

                if (resetting) selectRunner('', data[0].id, data);
            }
        },
        250
    );

    const selectRunner = useCallback(
        (name: string, value: string, options: DrawerRunnerInterface[]) => {
            const hardware = options.find((option) => option.id == value);

            if (hardware) {
                const drawer = values.drawers[Number(runnerIndex)];
                if (drawer) {
                    runnerWrites(
                        runnerIndex,
                        drawer,
                        fieldName,
                        value,
                        hardware,
                        isEditingProduct
                    ).forEach(([path, fieldValue]) => {
                        void setFieldValue(path, fieldValue);
                    });
                }
            }
        },
        [setFieldValue, runnerIndex, fieldName, isEditingProduct, values]
    );

    const errorMessage = useMemo(() => {
        const errors = messages?.errors;
        if (errors && Array.isArray(errors)) {
            return errors.find((error) =>
                validationErrorAppliesToField(
                    error,
                    'drawer_runner_specs',
                    runnerIndex
                )
            )?.message;
        }

        return null;
    }, [messages]);

    useEffect(() => {
        if (params) {
            void triggerGetRunners(params);
        }
    }, [params]);

    useEffect(() => {
        if (runnerReset === resetSeen.current) return;

        resetSeen.current = runnerReset;

        if (isDrawerComponent) return;

        resetPending.current = true;

        // a face the preset left alone asks for nothing new by itself
        if (params) void triggerGetRunners(params);
    }, [runnerReset]);

    useEffect(() => {
        if (isDrawerComponent && runners && value) {
            if (runners.length) {
                const runner = runners.find((option) =>
                    runnerIdsMatch(option.id, value)
                );
                const validationData = getValidationData('drawer_runner_specs');
                if (runner && isEmpty(validationData)) {
                    // set validation data once for the on load of update product
                    addValidationData('drawer_runner_specs', {
                        height: runner.runnerHeight,
                        depth: runner.runnerDepth,
                    });
                }
            } else {
                addValidationData('drawer_runner_specs', {height: 0, depth: 0});
                void setFieldValue(fieldName, '');
            }
        }
    }, [value, runners]);

    // effect to set runner default value
    useEffect(() => {
        if (!runners?.length) return;
        const runner = runners?.find((option) =>
            runnerIdsMatch(option.id, value)
        );

        if (runner && cabinetId !== undefined) {
            if (runner.id !== value) {
                selectRunner('', runner.id, runners);
            }
            return;
        }

        // Initialize previous depth if not set
        if (cabinetId !== undefined && prevDepthRef.current === undefined) {
            prevDepthRef.current = Number(params.depth);
        }

        const prevDepth = prevDepthRef.current;
        const currentDepth = params.depth;

        // Update previous depth if runner is missing or depth has changed
        if (!runner || prevDepth !== currentDepth) {
            prevDepthRef.current = currentDepth;
            const defaultRunner = runners[0];
            if (isDrawerComponent) {
                if (defaultRunner) {
                    addValidationData('drawer_runner_specs', {
                        height: defaultRunner.runnerHeight,
                        depth: defaultRunner.runnerDepth,
                    });
                    selectRunner('', defaultRunner.id, runners);
                }
            } else {
                if (runnerIndex !== undefined) {
                    selectRunner('', defaultRunner.id, runners);
                }
            }
        }
    }, [runners, value]);

    // effect to set drawer hardware value
    useEffect(() => {
        if (values?.drawers?.length && runners?.length && value) {
            const drawer = values.drawers[Number(runnerIndex)];
            if (
                drawer &&
                (!drawer?.hardware?.length ||
                    !drawer?.hardware?.[0]?.hardware_id)
            ) {
                const runner = runners?.find((option) =>
                    runnerIdsMatch(option.id, value)
                );

                const runnerHardware = {
                    hardware_id: runner?.hardware_id,
                    hardware: {
                        type: HardwareType.RUNNER,
                    },
                };
                void setFieldValue(`drawers[${runnerIndex}].hardware`, [
                    runnerHardware,
                ]);
                if (isDrawerComponent) {
                    void setFieldValue(
                        `drawers[${runnerIndex}].drawer_runner_height`,
                        runner?.runnerHeight
                    );
                    void setFieldValue(
                        `drawers[${runnerIndex}].drawer_runner_specs`,
                        runner?.name
                    );
                }
            }
        }
    }, [runners, value, values]);

    return (
        <FormGroup>
            <Form.Label column md={labelColSpan} xs={12} style={{zIndex: 1}}>
                Runner
                <span>*</span>
            </Form.Label>
            <Col md={7} xs={12}>
                <Tooltip
                    placement="right"
                    className="error-popover"
                    show={!!errorMessage}
                    tooltip={errorMessage}>
                    <SelectField
                        name={`drawers[${runnerIndex}.${fieldName}]`}
                        placeholder="No runners small enough to fit in cabinet depth"
                        options={runners?.map((runner) => ({
                            ...runner,
                            label: runner.name,
                            value: String(runner.id),
                        }))}
                        isLoading={isFetching || isLoading}
                        invalid={!!errorMessage || !runners?.length}
                        isDisabled={!!errorMessage || !runners?.length}
                        customOnChange={selectRunner}
                    />
                </Tooltip>
            </Col>
        </FormGroup>
    );
};
